import { h, Fragment } from 'preact';
import { useState, useRef, useEffect, useMemo } from 'preact/hooks';
import Panel from 'utils/panel';
import updateURL from 'utils/update-url';
import Icon from 'utils/icon';
import { formatMoney } from 'utils/storefront/format-resource';
import { withCartContext } from './cart-context';
import CartItem from './cart-item';
import CartAffirm from './cart-affirm';
import { checkPreventPurchase } from './utils';
import ProductRecommendation from './product-recommendation';

const Cart = (props) => {
  const { cart, data } = props;
  const { giftWrappingProduct } = data;
  const emptyCartMessage = data?.emptyCartMessage;
  const emptyCartCTA = data?.emptyCartCTA;
  const emptyCartURL = data?.emptyCartURL;
  const hideEngravingProduct = data?.hideEngravingProduct;
  const enableProductRecommendations = data?.enableProductRecommendations;
  const hasLines =
    cart?.state?.lines?.length > 0 || cart.state?.items?.length > 0;
  const subtotal =
    parseFloat(cart.state?.subtotal?.amount) ||
    parseFloat(cart.state?.total_price) / 100;
  const [threshold, setThreshold] = useState(0);
  const [thresholdPercent, setThresholdPercent] = useState(0);
  const thresholdEnabled = data.enableThreshold;
  const closeTriggerEl = useRef(null);
  const isCartRedirect = new URLSearchParams(window.location.search).get(
    'cart',
  );
  const checkoutTrigger = useRef(null)
  const [panelTriggers, setPanelTriggers] = useState({
    openTriggerEl: document.querySelector('[data-header-cart-trigger]'),
    closeTriggerEl: null,
  });
  const [preventedPurchaseItem, setPreventedPurchaseItem] = useState(null);
  const [isFocusable,setIsFocusable] = useState(false);

  const displayItemCount = useMemo(() => {
    if (!cart?.state?.items) return 0;
    
    if (!hideEngravingProduct) return cart.state.item_count;

    const engravingItemsCount = cart.state.items.reduce(
      (count, line) =>
        count +
        ((line.properties?._EngravingProduct || 
          line.properties?._EmbossingProduct) ? line.quantity : 0),
      0
    );
    
    return Math.max(0, cart.state.item_count - engravingItemsCount);
  }, [cart?.state?.items, cart?.state?.item_count, hideEngravingProduct]);

  useEffect(() => {
    const isInCart = new URLSearchParams(window.location.search).has('cart');
    const isCartReady = cart?.state?.item_count !== undefined;
  
    if (isInCart && isCartReady) {
      document.dispatchEvent(new CustomEvent('cart:open'));
  
      const url = new URL(window.location.href);
      url.searchParams.delete('cart');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, [cart]);

  useEffect(() => {
    const fetchPreventPurchase = async () => {
      if (!cart.state?.items) return;
      const { preventedItem } =
        await checkPreventPurchase(null, cart.state, true);
      setPreventedPurchaseItem(preventedItem);
    };
    fetchPreventPurchase();
  }, [cart])

  useEffect(() => {
    if (!closeTriggerEl.current) return;
    setPanelTriggers((value) => ({
      ...value,
      closeTriggerEl: closeTriggerEl.current,
    }));
  }, [closeTriggerEl]);
  const handleCheckoutClick = () => {
    window.location = updateURL('/checkout');
  };

  useEffect(() => {
    const thresholdLeft = data.shippingThreshold - subtotal;
    const percent = (subtotal / data.shippingThreshold) * 100;
    setThreshold(thresholdLeft);
    setThresholdPercent(percent);
  }, [subtotal]);

  const shippingThreshold = useMemo(
    () => (
      <div className="relative flex flex-col justify-center gap-2 px-5 py-4 bg-grey-7">
        {threshold < 0 && (
          <p className="text-black paragraph-5 flex items-center justify-start gap-[9px]">
            <span>{window.TMW?.Strings?.cart?.thresholdReached}</span>
          </p>
        )}
        {threshold > 0 && (
          <div>
            <p className="text-black paragraph-5">
              {window.TMW?.Strings?.cart?.threshold?.replace(
                '%threshold',
                formatMoney({
                  amount: threshold,
                  currencyCode: window.TMW?.localization?.currencyCode || 'USD',
                }),
              )}
            </p>
            <div className="relative w-full h-2 mt-2 bg-white">
              <div
                className="absolute top-0 left-0 h-2 transition-all bg-black"
                style={{ width: `${thresholdPercent}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
    ),
    [threshold, thresholdPercent]
  );
  const focusHandle = (focusedItem, e) => {
      if (!e.shiftKey && e.key === 'Tab') {
        e.preventDefault();
        focusedItem.focus();
      } 
  };

  const backFocusHandle = (focusedItem, e) => {
      if (e.shiftKey && e.key === 'Tab') {
        e.preventDefault();
        focusedItem.focus();
      } 
  };
  
  return (
    <Panel overlay={true} overlayId='cart' triggers={panelTriggers} 
    defaultOpen={isCartRedirect} context="cart" 
    childrenVisible={setIsFocusable} >
      <div className='h-[100dvh] flex flex-col overflow-auto scrollbar-hide sm:overflow-visible' >
        <div className="static sm:sticky flex items-center justify-between px-3.5 py-5 border-b border-grey-3">
          <p className="font-medium uppercase utility-1">
            {window.TMW.Strings.cart.title} {displayItemCount === 0
              ? null
              : `(${displayItemCount})`}
          </p>
          <button type="button" ref={closeTriggerEl} tabIndex="-1" aria-label='Close cart'
            onKeyDown={(e) => backFocusHandle(checkoutTrigger.current, e)}
           >
            <Icon name="close" />
            <span className="sr-only">
            {window.TMW.Strings.cart.close} {window.TMW.Strings.cart.title}
            </span>
          </button>
        </div>
        {displayItemCount !== 0 ?
          <Fragment>
            {thresholdEnabled && hasLines && shippingThreshold}
            <div 
              className={`
                sm:overflow-auto 
                sm:flex-grow
                sm:scrollbar-hide`
              }            >
              <div className={`
                flex flex-col 
                ${thresholdEnabled && hasLines ? "h-[calc(100vh-221px)]" : "h-full sm:h-[calc(100vh-177px)]"} 
                divide-y md:divide-y-0`
              }
              >
                <div className={`flex flex-col flex-1 gap-6 p-5 ${!isFocusable ? 'hidden' : ''}`}>
                  {cart?.state?.items?.map((line) => {
                    if (hideEngravingProduct &&
                       line.properties._EngravingProduct || 
                       hideEngravingProduct && 
                       line.properties._EmbossingProduct) return null;
                    return (
                    <CartItem
                      key={line.id}
                      line={line}
                      giftWrappingProduct={giftWrappingProduct}
                      preventedPurchaseItem={preventedPurchaseItem}
                    />
                    )
                  })}
                </div>
                {enableProductRecommendations &&
                  (<ProductRecommendation {...props} />)}
              </div>
            </div>
            <div className="static sm:sticky flex flex-col gap-4 p-5 border-t border-grey-3">
              <div className="flex justify-between uppercase utility-2">
                <span>
                  {window.TMW.Strings.cart.summary.subtotal}
                </span>
                {formatMoney(
                  cart?.state?.total_price
                    ? { amount: cart.state.total_price }
                    : { amount: 0 }, true
                )}
              </div>
              <button
                className="justify-center w-full no-underline button button--dark"
                type="button"
                onClick={handleCheckoutClick}
                disabled={preventedPurchaseItem !== null}
                onKeyDown={(e) => focusHandle(closeTriggerEl.current, e)}
                ref={checkoutTrigger}
              >
                {window.TMW.Strings.cart.summary.checkout}
              </button>
              {!data?.hideAffirmMessage &&
                <div>
                  <CartAffirm
                    totalPrice={cart?.state?.total_price}
                    affirmThreshold={data?.affirmThreshold}
                  />
                </div>
              }
            </div>
          </Fragment> :
          <div className='flex flex-col gap-4 px-16 py-20'>
            {emptyCartMessage &&
              <p className='text-center text-primary paragraph-2'>
                {emptyCartMessage}
              </p>
            }
            {emptyCartCTA && emptyCartURL &&
              <a href={emptyCartURL} className='flex pl-5 pr-5 no-underline button button--dark'>
                {emptyCartCTA}
              </a>
            }
          </div>
        }
      </div>
    </Panel>
  );
};

export default withCartContext(Cart);
